import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialMaterializedPipeline } from "./materializedPipelineState";
import {
  ApiException,
  DataEncodingClient,
  DataEncodingType,
  ErrorResponse,
  IDataEncodingClient,
  MaterializedPipelineCommand,
  MaterializeDto,
  PollingStatuses,
  UpdateEncodingStatusCommand,
} from "app/V2ApiClient";
import { LoadingState } from "types/loadingState";
import { IState } from "slices";
import { tryGetValueFromJson } from "utils/parseHelper";

const dataEncodingClient: IDataEncodingClient = new DataEncodingClient(process.env.REACT_APP_API_URL);
const API_VERSION = "2";

export const getMaterializedPipelineHistory = createAsyncThunk(
  "dataEncoding/getMaterializedPipelineHistory",
  async (projectId: string, { dispatch, rejectWithValue }) => {
    try {
      const result = await dataEncodingClient.getEncodingStatusByProjectId(
        projectId,
        DataEncodingType.MaterializedPipeline,
        API_VERSION,
      );
      if (result.status === PollingStatuses.InProgress) {
        // if in history it was in progress
        // then we need to retrieve the status again and check
        dispatch(
          setMaterializedPipelineHistory({
            token: result.token,
            status: result.status,
          }),
        );
        dispatch(checkAndUpdateMaterializedPipelineStatus());
      }

      return result;
    } catch (error) {
      ApiException.isApiException(error);

      return rejectWithValue(error);
    }
  },
);

export const triggerMaterializedPipeline = createAsyncThunk(
  "dataEncoding/materializedPipeline",
  async (command: MaterializedPipelineCommand, { signal, rejectWithValue }) => {
    try {
      const result = await dataEncodingClient.materializedPipeline(API_VERSION, command);
      if (signal.aborted) {
        return rejectWithValue("Request aborted");
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const checkAndUpdateMaterializedPipelineStatus = createAsyncThunk(
  "dataEncoding/checkAndUpdateMaterializedPipelineStatus",
  async (_, { dispatch, getState, signal }) => {
    const { materializedPipelineHistory } = (getState() as IState).materializedPipeline;
    const { token, status: currentStatus } = materializedPipelineHistory || {};

    if (!token || !materializedPipelineHistory) {
      return;
    }

    try {
      const statusDetails = await dataEncodingClient.getEncodingStatus(token, API_VERSION);
      const status = statusDetails?.status;

      if (signal.aborted) {
        return;
      }

      if (currentStatus !== status) {
        // update status
        const command: UpdateEncodingStatusCommand = {
          status,
          token,
          errorMessage: statusDetails?.message,
        };
        const result = await dataEncodingClient.updateEncodingStatus(API_VERSION, command);
        if (signal.aborted) {
          return;
        }

        const materializeHistory: MaterializeDto = {
          ...materializedPipelineHistory,
          status: result.status,
        };

        dispatch(setMaterializedPipelineHistory(materializeHistory));
      }
    } catch (error) {
      let errorMessage;
      if (ApiException.isApiException(error)) {
        const { response, message } = error;
        const parsedResponse = JSON.parse(response) as ErrorResponse;
        errorMessage = tryGetValueFromJson(parsedResponse.errorMessage ?? message, "detail");
      }
      const result = await dataEncodingClient.updateEncodingStatus(API_VERSION, {
        status: PollingStatuses.Failed,
        token,
        errorMessage: errorMessage,
      });
      dispatch(
        setMaterializedPipelineHistory({
          ...materializedPipelineHistory,
          status: result.status,
          errorMessage: errorMessage,
        }),
      );
    }
  },
);

const materializedPipelineSlice = createSlice({
  name: "materializedPipeline",
  initialState: initialMaterializedPipeline,
  reducers: {
    resetMaterializedPipelineHistory: () => initialMaterializedPipeline,
    setMaterializedPipelineHistory: (state, { payload }: PayloadAction<MaterializeDto | null>) => {
      state.materializedPipelineHistory = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(triggerMaterializedPipeline.pending, (state) => {
        state.materializedPipelineLoading = LoadingState.pending;
      })
      .addCase(triggerMaterializedPipeline.fulfilled, (state, action) => {
        state.materializedPipelineLoading = LoadingState.succeed;
        state.materializedPipelineHistory = action.payload;
      })
      .addCase(triggerMaterializedPipeline.rejected, (state) => {
        state.materializedPipelineLoading = LoadingState.failed;
      })
      // getMaterializedPipelineHistory
      .addCase(getMaterializedPipelineHistory.pending, (state) => {
        state.materializedPipelineLoading = LoadingState.pending;
      })
      .addCase(getMaterializedPipelineHistory.fulfilled, (state) => {
        state.materializedPipelineLoading = LoadingState.succeed;
      })
      .addCase(getMaterializedPipelineHistory.rejected, (state) => {
        state.materializedPipelineLoading = LoadingState.failed;
      });
  },
});
export const { resetMaterializedPipelineHistory, setMaterializedPipelineHistory } = materializedPipelineSlice.actions;
export default materializedPipelineSlice.reducer;
